import type { JsonValue } from '../types'

/**
 * Parse a JSON-Pointer (RFC 6901) like "#/foo/bar/0" into its path tokens.
 * Returns null if the pointer is malformed.
 */
export function parsePointer(pointer: string): string[] | null {
  if (typeof pointer !== 'string') return null
  let p = pointer
  if (p.startsWith('#')) p = p.slice(1)
  if (p === '' || p === '/') return []
  if (!p.startsWith('/')) return null
  return p
    .slice(1)
    .split('/')
    .map((token) => token.replace(/~1/g, '/').replace(/~0/g, '~'))
}

/**
 * Resolve a JSON-Pointer against a root value. Returns the target or null if
 * any segment is missing.
 */
export function resolvePointer(root: JsonValue, pointer: string): JsonValue | null {
  const tokens = parsePointer(pointer)
  if (tokens === null) return null
  let cur: JsonValue = root
  for (const t of tokens) {
    if (cur === null || typeof cur !== 'object') return null
    if (Array.isArray(cur)) {
      const idx = Number(t)
      if (!Number.isInteger(idx) || idx < 0 || idx >= cur.length) return null
      cur = cur[idx]
    } else {
      if (!(t in cur)) return null
      cur = cur[t]
    }
  }
  return cur
}

/**
 * True iff v is a { $ref: "…" } singleton object.
 */
export function isRefObject(v: unknown): v is { $ref: string } {
  return (
    v !== null &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    Object.keys(v as object).length === 1 &&
    typeof (v as { $ref?: unknown }).$ref === 'string'
  )
}

/**
 * Convert a path (array of keys/indices) into a JSON-Pointer string.
 */
export function pathToPointer(path: (string | number)[]): string {
  if (path.length === 0) return '#/'
  return '#/' + path.map((p) => String(p).replace(/~/g, '~0').replace(/\//g, '~1')).join('/')
}
